/// <amd-module name="Repos/Discussion/Editor/Widgets/ChangesTreeDiscussionWidget" />
import { IChangeWithDiscussionThread } from "Repos/Changes/Contracts";
import { IDiffDiscussionWidget, IDiscussionThreadModel } from "Repos/Discussion/Common/DiscussionContracts";
import { ITreeColumn } from "VSSUI/TreeEx";
import { ITreeItem, ITreeItemEx, ITreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";

export interface IChangesTreeDiscussionWidgetProps {
    extractFilePath: (threadModel: IDiscussionThreadModel) => string | undefined;
    getLineNumber: (threadModel: IDiscussionThreadModel) => number | undefined;
    isOriginalSide: (threadModel: IDiscussionThreadModel) => boolean | undefined;
    fileItemsProvider: ITreeItemProvider<IChangeWithDiscussionThread>;
    renderCell: (
        rowIndex: number,
        columnIndex: number,
        treeColumn: ITreeColumn<IChangeWithDiscussionThread>,
        treeItem: ITreeItemEx<IChangeWithDiscussionThread>,
        ariaRowIndex?: number
    ) => JSX.Element;
}

export class ChangesTreeDiscussionWidget implements IDiffDiscussionWidget {
    private props: IChangesTreeDiscussionWidgetProps;
    private pathsByThreadId: { [id: number]: string } = {};
    private filesByPath: { [path: string]: ITreeItem<IChangeWithDiscussionThread> } = {};

    public hasThreads: () => false;
    public getThreadCount: () => 0;

    constructor(props: IChangesTreeDiscussionWidgetProps) {
        this.props = props;

        // Store tree nodes by path to make accessing easier and faster when adding/removing later
        for (const item of this.props.fileItemsProvider.value) {
            this.filesByPath[item.underlyingItem.data.path] = item.underlyingItem;
        }
    }

    public addThread(thread: IDiscussionThreadModel): void {
        const { extractFilePath, getLineNumber, isOriginalSide, renderCell } = this.props;
        const filePath = extractFilePath(thread);
        if (filePath) {
            this.pathsByThreadId[thread.id] = filePath;
            this.applyChange(filePath, (fileItem, fileItemsProvider) => {
                // Make sure discussions under file item are visible
                fileItem.expanded = true;
                const threadData: IChangeWithDiscussionThread = {
                    ...fileItem.data,
                    addToOriginalSide: isOriginalSide(thread),
                    lineNumber: getLineNumber(thread),
                    renderCell,
                    thread
                };

                fileItemsProvider.add({ data: threadData }, fileItem, fileItem.childItems && findInsertAfter(fileItem.childItems, threadData));
            });
        }
    }

    public belongsToLine(): boolean {
        // Not inetersted in lines when integrating with changes tree.
        return false;
    }

    public getLineNumber(): number {
        return -1;
    }

    public isAttachedToFile(): boolean {
        return false;
    }

    public containsThread(threadId: number): boolean {
        const filePath = this.pathsByThreadId[threadId];
        const fileItem = this.findFileItem(filePath);
        if (fileItem) {
            return (fileItem.childItems || []).some(t => t.data.thread && t.data.thread.id === threadId);
        } else {
            // Try to find thread by looking each node. This is the case where a new thread is saved and its id is changed
            // but not updated in this.pathsByThreadId
            const threadItem = this.findThreadItem(threadId);
            if (threadItem) {
                this.pathsByThreadId[threadId] = threadItem.data.path;
                return true;
            }
        }

        return false;
    }

    public dispose = () => {
        for (const threadId in this.pathsByThreadId) {
            const filePath = this.pathsByThreadId[threadId];
            this.applyChange(filePath, (fileItem, fileItemsProvider) => {
                const threadItems = fileItem.childItems || [];
                for (let i = threadItems.length - 1; i >= 0; i--) {
                    fileItemsProvider.remove(threadItems[i], fileItem);
                }
            });
        }

        // @ts-ignore TypeScript 4.0 upgrade
        delete this.props;
        // @ts-ignore TypeScript 4.0 upgrade
        delete this.pathsByThreadId;
        // @ts-ignore TypeScript 4.0 upgrade
        delete this.filesByPath;
    };

    public removeThread(threadId: number): void {
        let filePath = this.pathsByThreadId[threadId];
        if (!filePath) {
            // This is a newly saved discussion thread. Find the file path hard way.
            const threadItem = this.findThreadItem(threadId);
            if (threadItem) {
                filePath = threadItem.data.path;
            }
        }

        this.applyChange(filePath, (fileItem, fileItemsProvider) => {
            const threadItems = fileItem.childItems || [];
            const threadItem = threadItems.filter(t => t.data.thread && t.data.thread.id === threadId)[0];
            if (threadItem) {
                fileItemsProvider.remove(threadItem, fileItem);
            }
        });
    }

    private applyChange(
        filePath: string,
        changeToApply: (fileItem: ITreeItem<IChangeWithDiscussionThread>, fileItemsProvider: ITreeItemProvider<IChangeWithDiscussionThread>) => void
    ): void {
        const { fileItemsProvider } = this.props;
        const fileItem = this.findFileItem(filePath);
        if (fileItem) {
            changeToApply(fileItem, fileItemsProvider);
        }
    }

    private findFileItem(filePath: string): ITreeItem<IChangeWithDiscussionThread> | undefined {
        return this.filesByPath[filePath];
    }

    private findThreadItem(threadId: number): ITreeItem<IChangeWithDiscussionThread> | undefined {
        const { fileItemsProvider } = this.props;
        return fileItemsProvider.value
            .filter(i => i.underlyingItem.data.thread && i.underlyingItem.data.thread.id === threadId)
            .map(i => i.underlyingItem)[0];
    }
}

function findInsertAfter(items: ITreeItem<IChangeWithDiscussionThread>[], newData: IChangeWithDiscussionThread) {
    // This uses lineNumber in original or modified side, which may not match the order of comments in the file.
    // But using the accurate displayLineNumber from DiffBlock adds more concerns,
    // like it needs to fetch diff per file before loading the tree, or reordering comments in the tree afterwards;
    // or like that diff may not be needed (Monaco diffs itself locally), so we'd need another approach or fetch them anyway.
    // Thus we're good with this cheaper approach to sort comments on the tree.
    const newLineNumber = newData.lineNumber || 0;
    let candidate: ITreeItem<IChangeWithDiscussionThread> | undefined;
    let candidateLineNumber = 0;
    for (const item of items) {
        const lineNumber = item.data.lineNumber || 0;
        if (lineNumber <= newLineNumber && lineNumber >= candidateLineNumber) {
            candidate = item;
            candidateLineNumber = lineNumber;
        }
    }

    return candidate;
}
